<template>
  <div :class="$style.container">
    <img
      :src="imageUrl"
      alt="Offer accepted illustration"
      :class="$style.image"
    />
    <BaseHeading size="l" mb mt :class="$style.heading">
      {{
        $t(
          'subscription.cancelation_flow.success_treatwell_offer.description_updated'
        )
      }}
    </BaseHeading>
    <BaseText v-if="loading" :class="$style.description">
      {{ $t('generic.loading') }}...
    </BaseText>
    <BaseText v-else :class="$style.description">
      {{
        $t(
          'subscription.cancelation_flow.success_treatwell_offer.description_activation',
          {
            date: filters.date(
              dayjs(activeSubscription?.periodEnd),
              { format: 'long' }
            )
          }
        )
      }}
    </BaseText>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import filters from '@/filters';
import dayjs from '@/dayjs';
import { useQuery } from '@vue/apollo-composable';
import { GET_STRIPE_SUBSCRIPTIONS_QUERY } from '../graphql';
import type { StripeSubscription } from '@/types';
import { StripeSubscriptionStatus } from '@/types';

interface StripeSubscriptionsQueryResult {
  stripeSubscriptions: StripeSubscription[];
}

const { result: stripeSubscriptionsData, loading } =
  useQuery<StripeSubscriptionsQueryResult>(GET_STRIPE_SUBSCRIPTIONS_QUERY);

const activeSubscription = computed(() => {
  if (loading.value) {
    return null;
  }
  const subscriptions = stripeSubscriptionsData.value?.stripeSubscriptions;
  if (!subscriptions || subscriptions.length === 0) {
    return null;
  }
  const foundSubscription = subscriptions.find(
    (sub: StripeSubscription) => sub.status === StripeSubscriptionStatus.Active,
  );

  if (!foundSubscription || !foundSubscription.periodEnd) {
    return null;
  }

  const sevenDaysFromNow = dayjs().add(7, 'days');
  const originalPeriodEnd = dayjs(foundSubscription.periodEnd);

  // Create a new object to avoid mutating the original subscription from the cache
  // and to allow adjustment of periodEnd.
  const adjustedSubscription: StripeSubscription = {
    ...foundSubscription,
    periodEnd: originalPeriodEnd.isBefore(sevenDaysFromNow)
      ? sevenDaysFromNow.toISOString()
      : foundSubscription.periodEnd,
  };

  return adjustedSubscription;
});

const imageUrl = '/img/cancel-subscription/treatwell_offer.svg';
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.image {
  margin-bottom: 1.5rem; // mb-6
  height: 10rem; // h-40
  width: auto;
}
</style> 
